import { PayeeRequirement } from "@wingspanhq/wingspan-api-sdk";

export type EmployeeTaxFormsMetadata = PayeeRequirement["dataSourceMetadata"];

/**
 * The EmployeeTaxForms data source embeds its progress on the requirement
 * itself (`dataSourceMetadata: { applicableFormsCount, completedFormsCount }`)
 * — there is no separate document to fetch. Tolerates absent metadata (a
 * requirement read before its first data-source refresh) and the older
 * `applicableForms` / `completed` key spellings from earlier deployments.
 */
export const getEmployeeTaxFormsProgress = (
  metadata: EmployeeTaxFormsMetadata
): { completed: number; total: number; label: string } => {
  const total =
    typeof metadata?.applicableFormsCount === "number"
      ? metadata.applicableFormsCount
      : typeof metadata?.applicableForms === "number"
        ? metadata.applicableForms
        : 0;
  const completed =
    typeof metadata?.completedFormsCount === "number"
      ? metadata.completedFormsCount
      : typeof metadata?.completed === "number"
        ? metadata.completed
        : 0;
  return {
    completed,
    total,
    label: total > 0 ? `${completed} of ${total} forms complete` : ""
  };
};

/**
 * The form instance ids the requirement's own last data-source refresh already
 * resolved (payments' `EmployeeTaxFormsRequirementService` writes these
 * alongside the progress counts above) — reading them here means the
 * checklist never needs its own pair-scoped lookup to discover which
 * instances apply.
 */
export const getEmployeeTaxFormsFormIds = (
  metadata: EmployeeTaxFormsMetadata
): string[] =>
  Array.isArray(metadata?.formIds)
    ? metadata.formIds.filter((id): id is string => typeof id === "string")
    : [];
